import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import api, { formatApiError } from '../lib/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-page-bg px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-10 h-10 rounded-[4px] bg-brand-orange flex items-center justify-center mx-auto mb-3">
            <span className="font-display text-white text-lg font-extrabold">S</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink">Reset Your Password</h1>
        </div>

        <div className="glass-panel p-6" data-testid="forgot-password-card">
          {sent ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-status-green mx-auto mb-3" strokeWidth={1.5} />
              <h2 className="font-display text-lg font-extrabold text-ink mb-2">Check your email</h2>
              <p className="font-body text-sm text-ink/50 mb-4">If <strong>{email}</strong> is registered, we have sent a password reset link to that address. The link will expire in 60 minutes for your security.</p>
              <Link to="/login" className="font-body text-sm text-brand-orange underline">Back to Sign In</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="font-body text-sm text-ink/50">Enter the email address associated with your account. If it matches a registered user, we will send a secure password reset link.</p>
              {error && <div className="bg-status-red/10 border border-status-red/20 text-status-red px-3 py-2 rounded-[4px] text-xs font-body">{error}</div>}
              <div>
                <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/70">Work Email</Label>
                <Input data-testid="forgot-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="name@organisation.gov.uk" className="mt-1 font-body text-sm" />
              </div>
              <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full bg-brand-orange text-white hover:bg-brand-orange/90 font-body font-medium rounded-[4px]">
                <Mail className="w-4 h-4 mr-2" /> {loading ? 'Sending…' : 'Send Reset Link'}
              </Button>
              <div className="text-center">
                <Link to="/login" className="inline-flex items-center gap-1 font-body text-xs text-ink/40 hover:text-ink/60">
                  <ArrowLeft className="w-3 h-3" /> Back to Sign In
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
